import { useEffect, useState } from "react";
import { getCarrierReadModel } from "../api/carrier";
import type { CarrierReadModel } from "../types/api";
import { subscribeLive } from "../aim/liveBus";

const empty: CarrierReadModel = { currentPosition: null, movementHistory: [] };
const apiBase = (
  (import.meta as unknown as { env: Record<string, string> }).env.VITE_AIM_API_BASE || ""
).replace(/\/$/, "");

// TARGET panel data: where the target_carrier (staff physically holding the
// Beacon) last reported being, plus movement history for this round.
// Refetches on the same shared SSE-or-poll signal as useNetwork/useHunters
// (apps/portal/src/aim/liveBus.ts) instead of its own independent timer —
// a stale/failed fetch just keeps the last known value on screen rather
// than blanking the panel, and never affects the other panels' hooks.
export function useCarrier(targetId: string, enabled: boolean) {
  const [data, setData] = useState<CarrierReadModel>(empty);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled || !targetId) {
      setData(empty);
      setError(false);
      return;
    }
    let stopped = false;
    const abort = new AbortController();
    async function refresh() {
      try {
        const next = await getCarrierReadModel(targetId, abort.signal);
        if (!stopped) {
          setData(next);
          setError(false);
        }
      } catch {
        if (!stopped) setError(true);
      }
    }
    void refresh();
    const unsubscribe = subscribeLive(apiBase, () => void refresh());
    return () => {
      stopped = true;
      abort.abort();
      unsubscribe();
    };
  }, [targetId, enabled]);

  return { data, error };
}
